import React, { useState, memo, useCallback } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import NotificationBell from './NotificationBell';
import NotificationDetailModal from './NotificationDetailModal';
import LanguageSelector from './LanguageSelector';
import type { NotificationData } from './NotificationBell';
import { presentationBackground } from '../theme/presentationBackground';

const drawerWidth = 280;

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
}

const Layout: React.FC<LayoutProps> = memo(({ children, title }) => {
  const { t } = useTranslation('pages');
  const displayTitle = title || t('dashboard.title');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<NotificationData | null>(null);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(!mobileOpen);
  }, [mobileOpen]);

  const handleDrawerClose = useCallback(() => {
    setMobileOpen(false);
  }, []);

  const handleNotificationClick = useCallback((notification: NotificationData) => {
    setSelectedNotification(notification);
    setNotificationModalOpen(true);
  }, []);

  const handleNotificationModalClose = useCallback(() => {
    setNotificationModalOpen(false);
  }, []);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sidebar open={mobileOpen} onClose={handleDrawerClose} />

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: 'transparent',
          minHeight: '100vh',
        }}
      >
        {/* Top AppBar for mobile */}
        {isMobile && (
          <AppBar
            position="fixed"
            sx={{
              width: '100%',
              ml: { md: `${drawerWidth}px` },
              background: presentationBackground.sidebarGradient,
              boxShadow: '0 2px 12px rgba(38,155,178,0.25)',
            }}
          >
            <Toolbar sx={{ minHeight: { xs: 56 } }}>
              <IconButton
                color="inherit"
                aria-label={t('dashboard.title')}
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 1.5 }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 700, fontSize: '1.05rem' }}>
                {displayTitle}
              </Typography>
              <LanguageSelector />
              <NotificationBell onNotificationClick={handleNotificationClick} />
            </Toolbar>
          </AppBar>
        )}

        {/* Top AppBar for desktop */}
        {!isMobile && (
          <AppBar
            position="fixed"
            sx={{
              width: { md: `calc(100% - ${drawerWidth}px)` },
              ml: { md: `${drawerWidth}px` },
              background: presentationBackground.sidebarGradient,
              color: '#ffffff',
              boxShadow: '0 2px 12px rgba(38,155,178,0.25)',
              borderBottom: 'none',
              backdropFilter: 'none',
            }}
          >
            <Toolbar sx={{ justifyContent: 'flex-end', minHeight: { md: 56 } }}>
              <LanguageSelector />
              <NotificationBell onNotificationClick={handleNotificationClick} />
            </Toolbar>
          </AppBar>
        )}

        {/* Content */}
        <Box
          className="ll-page-content"
          sx={{
            p: { xs: 2, sm: 3, md: 3.5 },
            mt: 7,
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Notification Detail Modal */}
      <NotificationDetailModal
        open={notificationModalOpen}
        onClose={handleNotificationModalClose}
        notification={selectedNotification}
      />
    </Box>
  );
});

Layout.displayName = 'Layout';

export default Layout; 